import { create } from 'zustand'
import type { CareAuditLog, AuditEntityType } from '@rondela/shared'
import { api, ApiError } from '../../shared/lib/api'

interface AuditFilters {
  petId?: string
  entityType?: AuditEntityType
  userId?: string
}

interface Pagination {
  page: number
  pageSize: number
  total: number
  totalPages: number
}

interface AuditState {
  auditLogs: CareAuditLog[]
  pagination: Pagination
  filters: AuditFilters
  isLoading: boolean
  error: string | null

  // Actions
  fetchAuditLogs: (page?: number, filters?: AuditFilters) => Promise<void>
  fetchPetAuditLogs: (petId: string, limit?: number) => Promise<void>
  setFilters: (filters: AuditFilters) => void
  clearFilters: () => void
  clearError: () => void
}

export const useAuditStore = create<AuditState>((set, get) => ({
  auditLogs: [],
  pagination: {
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0,
  },
  filters: {},
  isLoading: false,
  error: null,

  fetchAuditLogs: async (page = 1, filters?: AuditFilters) => {
    try {
      set({ isLoading: true, error: null })

      const currentFilters = filters || get().filters
      const params = new URLSearchParams()
      params.append('page', page.toString())
      params.append('pageSize', '50')

      if (currentFilters.petId) {
        params.append('petId', currentFilters.petId)
      }
      if (currentFilters.entityType) {
        params.append('entityType', currentFilters.entityType)
      }
      if (currentFilters.userId) {
        params.append('userId', currentFilters.userId)
      }

      const response = await api.get<{
        auditLogs: CareAuditLog[]
        pagination: Pagination
      }>(`/api/audit?${params.toString()}`)

      set({
        auditLogs: response.auditLogs,
        pagination: response.pagination,
        filters: currentFilters,
        isLoading: false,
      })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar bitácora'
      set({ error: message, isLoading: false })
    }
  },

  fetchPetAuditLogs: async (petId: string, limit = 50) => {
    try {
      set({ isLoading: true, error: null })

      const response = await api.get<{ auditLogs: CareAuditLog[] }>(
        `/api/audit/pet/${petId}?limit=${limit}`
      )

      set({
        auditLogs: response.auditLogs,
        isLoading: false,
      })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar bitácora'
      set({ error: message, isLoading: false })
    }
  },

  setFilters: (filters: AuditFilters) => {
    set({ filters })
  },

  clearFilters: () => {
    set({ filters: {} })
  },

  clearError: () => set({ error: null }),
}))
